/**
 * Small marks the Frameworks list and a framework's page share: the coverage
 * meter, the framework's tile, and the health label. Calm by default — the
 * meter is one hairline-thin bar, colour only where it carries meaning.
 *
 * @module components/regulation/frameworks/FrameworkGlyphs
 */
import { LayersIcon } from "lucide-react";

import { cn } from "~/lib/utils";
import type { Health } from "../monitor/frameworkRollup";
import { HEALTH, HealthIcon, LINEAR_GREEN, LINEAR_INDIGO } from "../monitor/linearGlyphs";
import { REQUIREMENT_STATUS_LABEL, type Coverage, type RequirementStatus } from "./frameworkView";

/** Fully covered reads green; anything short of it reads in the controls' indigo. */
export const coverageColor = (coverage: Coverage): string =>
  coverage.percent === 100 ? LINEAR_GREEN : LINEAR_INDIGO;

/** A thin bar and its percentage; a dash when nothing is catalogued. */
export function CoverageMeter({
  coverage,
  className,
  barClassName,
  showCount = false,
  hideValue = false,
}: {
  readonly coverage: Coverage;
  readonly className?: string;
  readonly barClassName?: string;
  /** Also print "7/16" after the percentage. */
  readonly showCount?: boolean;
  /** Only the bar — the percentage is printed beside it already. */
  readonly hideValue?: boolean;
}) {
  if (coverage.percent === null) {
    return (
      <span className={cn("text-[13px] text-muted-foreground/60", className)} title="No controls catalogued">
        —
      </span>
    );
  }
  return (
    <span
      className={cn("flex min-w-0 items-center gap-2.5", className)}
      title={`${coverage.satisfied} of ${coverage.total} controls addressed`}
    >
      <span
        role="meter"
        aria-label="Controls addressed"
        aria-valuemin={0}
        aria-valuemax={coverage.total}
        aria-valuenow={coverage.satisfied}
        className={cn("relative h-1.5 w-16 shrink-0 overflow-hidden rounded-full bg-foreground/[0.08]", barClassName)}
      >
        <span
          className="absolute inset-y-0 start-0 rounded-full transition-[width] duration-300 ease-out"
          style={{ width: `${coverage.percent}%`, backgroundColor: coverageColor(coverage) }}
        />
      </span>
      <span className={cn("text-[13px] tabular-nums text-foreground/85", hideValue && "sr-only")}>
        {coverage.percent}%
        {showCount ? (
          <span className="ms-1.5 text-xs text-muted-foreground">
            {coverage.satisfied}/{coverage.total}
          </span>
        ) : null}
      </span>
    </span>
  );
}

/** The framework's mark: a quiet tile around the layers icon. */
export function FrameworkTile({ className }: { readonly className?: string }) {
  return (
    <span
      aria-hidden
      className={cn(
        "flex size-8 shrink-0 items-center justify-center rounded-lg border border-border/60 bg-muted/50 text-muted-foreground",
        className,
      )}
    >
      <LayersIcon className="size-4" />
    </span>
  );
}

/** Linear's project health: the disc and the word, in the health's colour. */
export function HealthLabel({
  health,
  title,
  className,
}: {
  readonly health: Health;
  readonly title?: string;
  readonly className?: string;
}) {
  return (
    <span
      title={title}
      className={cn("inline-flex items-center gap-1.5 whitespace-nowrap text-[13px] font-medium", className)}
      style={{ color: HEALTH[health].color }}
    >
      <HealthIcon health={health} />
      {HEALTH[health].label}
    </span>
  );
}

/**
 * A requirement's state as a 14px status circle: green check when covered,
 * an indigo pie as far as it goes when partial, an empty ring when nothing is
 * addressed, a dashed ring when it carries no catalogued controls.
 */
export function RequirementGlyph({
  status,
  coverage,
  className,
}: {
  readonly status: RequirementStatus;
  readonly coverage: Coverage;
  readonly className?: string;
}) {
  const label = REQUIREMENT_STATUS_LABEL[status];
  const box = cn("size-3.5 shrink-0", className);
  if (status === "covered") {
    return (
      <svg viewBox="0 0 14 14" role="img" aria-label={label} className={box}>
        <circle cx="7" cy="7" r="7" fill={LINEAR_GREEN} />
        <path d="M4.3 7.2 6.1 9 9.7 5.3" fill="none" stroke="var(--color-background)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }
  if (status === "uncatalogued") {
    return (
      <svg viewBox="0 0 14 14" role="img" aria-label={label} className={cn(box, "text-muted-foreground")}>
        <circle cx="7" cy="7" r="6" fill="none" stroke="currentColor" strokeWidth="1.5" strokeDasharray="1.4 1.74" />
      </svg>
    );
  }
  // A pie wedge from 12 o'clock, clockwise, as far as the coverage goes.
  const fraction = coverage.total > 0 ? coverage.satisfied / coverage.total : 0;
  const angle = fraction * 2 * Math.PI;
  const x = 7 + 3.5 * Math.sin(angle);
  const y = 7 - 3.5 * Math.cos(angle);
  const color = status === "partial" ? LINEAR_INDIGO : "var(--color-muted-foreground)";
  return (
    <svg viewBox="0 0 14 14" role="img" aria-label={label} className={box}>
      <circle cx="7" cy="7" r="6" fill="none" stroke={color} strokeWidth="1.5" />
      {fraction > 0 ? (
        <path d={`M7 7 L7 3.5 A3.5 3.5 0 ${fraction > 0.5 ? 1 : 0} 1 ${x.toFixed(3)} ${y.toFixed(3)} Z`} fill={color} />
      ) : null}
    </svg>
  );
}
